import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { forkJoin, Observable, of } from 'rxjs'
import { flatMap, map } from 'rxjs/operators'
import { environment } from '../../../../environments/environment'
import { RestrictedProductAccount } from '../../../shared/classes/restricted-product-account'
import { RestrictedProductAccountUser } from '../../../shared/classes/restricted-product-account-user'
import { AccountSearchParams } from '../../../shared/interfaces/account-search-params'
import { InviteAccountUser } from '../../../shared/interfaces/invite-account-user'
import {
  ApproveRestrictedProductAccount,
  PaginatedList,
  RequestRestrictedProductAccount,
  RestrictedProductAccountPolicy,
  User,
  UserPermissionBase,
} from '../../../shared/shared.interfaces'

@Injectable({
  providedIn: 'root',
})
export class RestrictedProductService {
  constructor(private httpClient: HttpClient, private router: Router, private activatedRoute: ActivatedRoute) {}

  getRestrictedProductsForUser(params): Observable<PaginatedList<RestrictedProductAccount> | null> {
    const headerDict = {
      Accept: 'application/json',
    }
    return this.getAccounts(params, headerDict)
  }

  getRestrictedProductsForAdmin(params): Observable<PaginatedList<RestrictedProductAccount> | null> {
    const headerDict = {
      Accept: 'application/allegion.dke.admin.restrictedproductaccounts+json',
    }
    return this.getAccounts(params, headerDict)
  }

  getRestrictedAccount(accountId: number): Observable<RestrictedProductAccount> {
    return this.httpClient.get<RestrictedProductAccount>(`${environment.restrictedProductUrl}/${accountId}`).pipe(
      map((account) => {
        return Object.assign(new RestrictedProductAccount(), account)
      })
    )
  }

  updateDefaultRestrictedAccount(accountId: number, props: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/allegion.dke.restrictedproductaddresscontacts+json',
    })
    const requestOptions = {
      headers,
    }
    return this.httpClient.put(`${environment.restrictedProductUrl}/${accountId}/addresses`, props, requestOptions)
  }

  updateRestrictedProductAccount(accountId: number, accountInfo: Partial<RestrictedProductAccount>): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    })
    const requestOptions = {
      headers,
    }
    return this.httpClient.put(`${environment.restrictedProductUrl}/${accountId}`, accountInfo, requestOptions)
  }

  extendAccountAccess(accountId: number): Observable<void> {
    return this.httpClient.post<void>(
      `${environment.usersUrl}/restrictedProductAccounts/${accountId}/permissionAccess`,
      null
    )
  }

  getUserPermissionsNoAdmins(accountId: number): Observable<RestrictedProductAccountUser[]> {
    return this.httpClient
      .get<{ userPermissions: RestrictedProductAccountUser[] }>(
        `${environment.restrictedProductUrl}/${accountId}/users`
      )
      .pipe(
        map((response) =>
          response.userPermissions.map((user) => Object.assign(new RestrictedProductAccountUser(), user))
        )
      )
  }

  addUserAccountPermission(userId: number, restrictedProductAccountId: number, permission: UserPermissionBase) {
    return this.httpClient.post(
      `${environment.restrictedProductUrl}/${restrictedProductAccountId}/users/${userId}/permissions`,
      permission
    )
  }

  updateUserAccountPermission(userId: number, restrictedProductAccountId: number, permission: UserPermissionBase) {
    return this.httpClient.put<void>(
      `${environment.restrictedProductUrl}/${restrictedProductAccountId}/users/${userId}/permissions`,
      permission
    )
  }

  deleteUserAccountPermission(
    userId: number,
    restrictedProductAccountId: number,
    permissionId: number
  ): Observable<void> {
    return this.httpClient.delete<void>(
      // tslint:disable-next-line: max-line-length
      `${environment.restrictedProductUrl}/${restrictedProductAccountId}/users/${userId}/permissions/${permissionId}`
    )
  }

  addUserToAccount(restrictedProductAccountId: number, invite: InviteAccountUser, permissions: UserPermissionBase[]) {
    return this.httpClient
      .post<User>(`${environment.restrictedProductUrl}/${restrictedProductAccountId}/users`, invite)
      .pipe(
        flatMap((user) =>
          permissions.some((_) => _ != null)
            ? forkJoin(
                permissions.map((permission) =>
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  this.addUserAccountPermission(user.id!, restrictedProductAccountId, permission)
                )
              )
            : of(null)
        )
      )
  }

  getUserAccountPermissions(restrictedProductAccountId: number): Observable<RestrictedProductAccountPolicy> {
    return this.httpClient.get<RestrictedProductAccountPolicy>(
      `${environment.usersUrl}/RestrictedProductAccounts/${restrictedProductAccountId}/Permissions`
    )
  }

  getProcomAccountByAccountNumber(accountNumber: string): Observable<any> {
    return this.httpClient.get(`${environment.procomAccountsUrl}/${accountNumber}`)
  }

  approveRestrictedProductAccount(
    restrictedProductAccountId: number,
    args: ApproveRestrictedProductAccount
  ): Observable<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/allegion.dke.restrictedproductaccount.approval+json',
    })
    const requestOptions = {
      headers,
    }
    return this.httpClient.put<void>(
      `${environment.restrictedProductUrl}/${restrictedProductAccountId}/approve`,
      args,
      requestOptions
    )
  }

  rejectRestrictedProductAccount(restrictedProductAccountId: number): Observable<void> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/allegion.dke.restrictedproductaccount.reject+json',
    })
    const requestOptions = {
      headers,
    }
    return this.httpClient.put<void>(
      `${environment.restrictedProductUrl}/${restrictedProductAccountId}/reject`,
      null,
      requestOptions
    )
  }

  private getAccounts(
    params: { [key: string]: any },
    headerDict: { Accept: string }
  ): Observable<PaginatedList<RestrictedProductAccount>> {
    const searchParams = new URLSearchParams()
    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key) && params[key] != null) {
        searchParams.append(key, params[key])
      }
    }

    const requestOptions = {
      headers: new HttpHeaders(headerDict),
    }

    const url = `${environment.usersUrl}/restrictedProductAccounts?${searchParams}`

    return this.httpClient
      .get<PaginatedList<AccountSearchParams>>(
        `${environment.usersUrl}/restrictedProductAccounts?${searchParams}`,
        requestOptions
      )
      .pipe(
        map((accounts) => {
          return {
            pageIndex: accounts.pageIndex,
            totalPages: accounts.totalPages,
            totalItems: accounts.totalItems,
            startItem: accounts.startItem,
            endItem: accounts.endItem,
            items: accounts.items.map((x) => {
              return Object.assign(new RestrictedProductAccount(), {
                name: x.accountName,
                procomAccountNumber: x.accountNumber,
                id: x.restrictedProductAccountId,
                addressLine1: x.addressLine1,
                city: x.city,
                country: x.country,
                createdOn: new Date(x.createdOn),
                level: x.level,
                organizationId: x.organizationId,
                organization: {
                  id: x.organizationId,
                  name: x.organizationName,
                  status: x.organizationStatus,
                },
                state: x.state,
                status: x.status,
                zipCode: x.zip,
                accessViewModel: x.accessViewModel,
              })
            }),
          }
        })
      )
  }

  requestAccount(request: RequestRestrictedProductAccount): Observable<void> {
    return this.httpClient.post<void>(`${environment.restrictedProductUrl}`, request)
  }

  changeAccountStatus(accountId, statusId) {
    const type = 'application/allegion.dke.restrictedproductaccount.update.status+json'

    const headers = new HttpHeaders().set('Content-Type', type)

    const request = {
      accountId: accountId,
      statusId: statusId,
    }

    const url = `${environment.restrictedProductUrl}/${accountId}/Statuses/${statusId}`

    return this.httpClient.patch<any>(url, request, { headers })
  }

  submitSupportTicket(request) {
    const formData = new FormData()
    let fileToUpload = <File>request.FormFile[0]
    formData.append('AccountName', request.AccountName)
    formData.append('AccountNumber', request.AccountNumber)
    formData.append('RequesterEmail', request.RequesterEmail)
    formData.append('RequesterName', request.RequesterName)
    formData.append('PhoneNumber', request.PhoneNumber)
    formData.append('Description', request.Description)
    formData.append('Subject', request.Subject)
    formData.append('Priority', request.Priority)
    formData.append('FormFile', fileToUpload)
    let headers
    headers = {
      headers: new HttpHeaders({
        'Content-Type': 'multipart/form-data',
      }),
    }

    return this.httpClient.post(`${environment.baseUrl}${environment.version1}/HelpDeskService/ticket`, formData, {
      headers,
    })
  }
}
